import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { deleteTarget, testTarget } from './alert-routing-api'
import { runHref } from './paths'
import { testOutcome } from './target-words'

// A target's Send test and Delete, the same from its row and from its drawer.
// Send test's answer is one status line (target-words.js testOutcome; the
// list's card notice, the drawer's status line). Delete asks first in the
// house confirmation with the scrim; onDeleted gets the target.
export const useTargetActions = ({ onDeleted } = {}) => {
  const [testing, setTesting] = useState(null) // the target id a test is running for
  const [outcome, setOutcome] = useState(null) // { targetID, name, tone, text, alertID, failure }
  const [asking, setAsking] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()

  const sendTest = useCallback(async target => {
    if (testing) return
    setTesting(target.id)
    setOutcome(null)
    try {
      const answer = await testTarget(target.id)
      setOutcome({ targetID: target.id, name: target.name, ...testOutcome(answer && answer.run, target) })
    } catch (err) {
      setOutcome({ targetID: target.id, name: target.name, tone: 'bad', text: serverMessage(err, "The test couldn't be sent."), failure: err })
    } finally {
      setTesting(null)
    }
  }, [testing])

  const askDelete = useCallback(target => { setError(null); setAsking(target) }, [setError])
  const clear = useCallback(() => setOutcome(null), [])

  const confirm = async () => {
    if (busy || !asking) return
    setBusy(true)
    setError(null)
    try {
      await deleteTarget(asking.id)
      const gone = asking
      setAsking(null)
      if (outcome && outcome.targetID === gone.id) setOutcome(null)
      if (onDeleted) onDeleted(gone)
    } catch (err) {
      setError(serverMessage(err, "The target couldn't be deleted."), err)
    } finally {
      setBusy(false)
    }
  }

  const dialogs = (
    <ConfirmDialog open={Boolean(asking)} scrim title={asking ? `Delete the target "${asking.name}"?` : ''} confirmText="Delete target" busy={busy}
      body="No route uses it. It stops at once and can't be brought back." error={error} failure={failure}
      onCancel={() => setAsking(null)} onConfirm={confirm} />
  )

  return { sendTest, testing, outcome, clear, askDelete, dialogs }
}

// A test's answer, one line: "Sent to #ops just now · View in run history",
// or the reason it failed. named: lead with the target's name (the list).
export const TestOutcomeLine = ({ outcome, named = false, testing = false }) => {
  if (testing) return <span className="sw-status" role="status">Sending a test…</span>
  if (!outcome) return null
  const tone = outcome.tone === 'ok' ? 'sw-tone-ok' : outcome.tone === 'bad' ? 'sw-tone-bad' : outcome.tone === 'warn' ? 'sw-tone-warn' : ''
  return (
    <span className="sw-status ar-test-line" role={outcome.tone === 'bad' ? 'alert' : 'status'} data-testid="test-outcome">
      <span className={tone}>{named ? `${outcome.name}: ${outcome.text}` : outcome.text}</span>
      {outcome.alertID && <> · <Link to={runHref(outcome.alertID)}>View in run history</Link></>}
      {outcome.failure && <ErrorReference error={outcome.failure} compact />}
    </span>
  )
}
